import { fail } from "@sveltejs/kit";
import { p as prisma } from "../../../../chunks/prisma.js";
import { a as legislationSchema } from "../../../../chunks/validationSchemas.js";
import { s as sendEmail } from "../../../../chunks/mailService.js";
const load = async ({ url }) => {
  const page = parseInt(url.searchParams.get("page") || "1");
  const limit = 10;
  const offset = (page - 1) * limit;
  const search = url.searchParams.get("search") || "";
  const where = search ? {
    OR: [
      { title: { contains: search, mode: "insensitive" } },
      { number: { contains: search, mode: "insensitive" } }
    ]
  } : {};
  const [legislations, totalCount] = await Promise.all([
    prisma.legislationItem.findMany({
      where,
      orderBy: [{ year: "desc" }, { type: "asc" }, { number: "asc" }],
      skip: offset,
      take: limit
    }),
    prisma.legislationItem.count({ where })
  ]);
  return {
    legislations,
    totalCount,
    page,
    totalPages: Math.ceil(totalCount / limit),
    search
  };
};
const actions = {
  create: async ({ request, url }) => {
    const formData = await request.formData();
    const data = {
      year: parseInt(formData.get("year")),
      type: formData.get("type"),
      number: formData.get("number") || null,
      title: formData.get("title") || null,
      parentStatute: formData.get("parentStatute") || null,
      sponsor: formData.get("sponsor") || null,
      house: formData.get("house") || null,
      legislativeEffects: formData.get("legislativeEffects") || null,
      dateGazetted: formData.get("dateGazetted") ? new Date(formData.get("dateGazetted")) : null,
      gazetteDetails: formData.get("gazetteDetails") || null,
      availabilityAt: formData.get("availabilityAt") ? new Date(formData.get("availabilityAt")) : null,
      uploadedAt: formData.get("uploadedAt") ? new Date(formData.get("uploadedAt")) : null,
      kgsPublicationAt: formData.get("kgsPublicationAt") ? new Date(formData.get("kgsPublicationAt")) : null,
      commencementAt: formData.get("commencementAt") ? new Date(formData.get("commencementAt")) : null,
      pagination: formData.get("pagination") || null,
      statusOnDatabase: formData.get("statusOnDatabase") || null,
      legislativeUpdates: formData.get("legislativeUpdates") || null,
      comments: formData.get("comments") || null,
      kenyaGazetteSuppNo: formData.get("kenyaGazetteSuppNo") || null,
      revocationsAmendments: formData.get("revocationsAmendments") || null,
      assentAt: formData.get("assentAt") ? new Date(formData.get("assentAt")) : null,
      dateAvailedAt: formData.get("dateAvailedAt") ? new Date(formData.get("dateAvailedAt")) : null,
      availability: formData.get("availability") || null
    };
    const result = legislationSchema.safeParse(data);
    if (!result.success) {
      const errors = {};
      for (const [key, value] of Object.entries(result.error.flatten().fieldErrors)) {
        errors[key] = value;
      }
      return fail(400, { data, errors });
    }
    try {
      const newLegislation = await prisma.legislationItem.create({
        data: result.data
      });
      void (async () => {
        try {
          const subscribers = await prisma.user.findMany({ select: { email: true } });
          if (subscribers.length === 0) return;
          const origin = url.origin;
          const legislationData = {
            title: newLegislation.title ?? "Untitled Legislation",
            category: newLegislation.type ?? "General",
            summary: newLegislation.legislativeUpdates || newLegislation.comments || "New legislation has been added to the Kenya Law database.",
            datePublished: newLegislation.dateGazetted?.toISOString() ?? (/* @__PURE__ */ new Date()).toISOString(),
            link: `${origin}/legislation/${newLegislation.id}`
          };
          await Promise.allSettled(
            subscribers.map(
              (subscriber) => sendEmail(
                subscriber.email,
                `New Legislation Published – ${legislationData.title}`,
                "new-legislation",
                { legislation: legislationData, origin }
              ).catch(
                (emailError) => console.error(`Failed to send email to ${subscriber.email}:`, emailError)
              )
            )
          );
        } catch (bgError) {
          console.error("Background email task failed:", bgError);
        }
      })();
      return { success: true, legislation: newLegislation };
    } catch (error) {
      console.error("Error creating legislation:", error);
      return fail(500, {
        data,
        errors: { _errors: ["Failed to create legislation item"] }
      });
    }
  },
  update: async ({ request, url }) => {
    const formData = await request.formData();
    const id = parseInt(formData.get("id"));
    const data = {
      year: parseInt(formData.get("year")),
      type: formData.get("type"),
      number: formData.get("number") || null,
      title: formData.get("title") || null,
      parentStatute: formData.get("parentStatute") || null,
      sponsor: formData.get("sponsor") || null,
      house: formData.get("house") || null,
      legislativeEffects: formData.get("legislativeEffects") || null,
      dateGazetted: formData.get("dateGazetted") ? new Date(formData.get("dateGazetted")) : null,
      gazetteDetails: formData.get("gazetteDetails") || null,
      availabilityAt: formData.get("availabilityAt") ? new Date(formData.get("availabilityAt")) : null,
      uploadedAt: formData.get("uploadedAt") ? new Date(formData.get("uploadedAt")) : null,
      kgsPublicationAt: formData.get("kgsPublicationAt") ? new Date(formData.get("kgsPublicationAt")) : null,
      commencementAt: formData.get("commencementAt") ? new Date(formData.get("commencementAt")) : null,
      pagination: formData.get("pagination") || null,
      statusOnDatabase: formData.get("statusOnDatabase") || null,
      legislativeUpdates: formData.get("legislativeUpdates") || null,
      comments: formData.get("comments") || null,
      kenyaGazetteSuppNo: formData.get("kenyaGazetteSuppNo") || null,
      revocationsAmendments: formData.get("revocationsAmendments") || null,
      assentAt: formData.get("assentAt") ? new Date(formData.get("assentAt")) : null,
      dateAvailedAt: formData.get("dateAvailedAt") ? new Date(formData.get("dateAvailedAt")) : null,
      availability: formData.get("availability") || null
    };
    const result = legislationSchema.safeParse(data);
    if (!result.success) {
      const errors = {};
      for (const [key, value] of Object.entries(result.error.flatten().fieldErrors)) {
        errors[key] = value;
      }
      return fail(400, { data, errors, id });
    }
    try {
      const updatedLegislation = await prisma.legislationItem.update({
        where: { id },
        data: result.data
      });
      const subscribers = await prisma.user.findMany({
        select: { email: true }
      });
      const sendNotifications = formData.get("sentnotifications") === "on";
      if (sendNotifications && subscribers.length > 0) {
        const origin = url.origin;
        const legislationData = {
          title: updatedLegislation.title ?? "Untitled Legislation",
          category: updatedLegislation.type ?? "General",
          summary: updatedLegislation.legislativeUpdates || updatedLegislation.comments || "A legislation item has been updated on Kenya Law.",
          dateUpdated: (/* @__PURE__ */ new Date()).toISOString(),
          link: `${origin}/legislation/${updatedLegislation.id}`
        };
        (async () => {
          await Promise.allSettled(
            subscribers.map(async (subscriber) => {
              try {
                await sendEmail(
                  subscriber.email,
                  `Legislation Updated – ${legislationData.title}`,
                  "legislation-update",
                  { legislation: legislationData, origin }
                );
                console.log(`✅ Sent update email to ${subscriber.email}`);
              } catch (err) {
                console.error(`❌ Failed to send to ${subscriber.email}:`, err);
              }
            })
          );
        })();
      } else {
        console.log("🔕 Notifications skipped");
      }
      return { success: true };
    } catch (error) {
      console.error("Error updating legislation:", error);
      return fail(500, {
        data,
        errors: { _errors: ["Failed to update legislation item"] },
        id
      });
    }
  },
  delete: async ({ request }) => {
    const formData = await request.formData();
    const id = parseInt(formData.get("id"));
    try {
      await prisma.legislationItem.delete({
        where: { id }
      });
      return { success: true };
    } catch (error) {
      console.error("Error deleting legislation:", error);
      return fail(500, {
        errors: { _errors: ["Failed to delete legislation item"] }
      });
    }
  }
};
export {
  actions,
  load
};
